// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Button, Form, Input, Tabs} from "antd";
import {CheckOutlined, MinusOutlined} from "@ant-design/icons";
import * as Setting from "../Setting";
import * as AuthBackend from "./AuthBackend";
// import * as ProviderButton from "./ProviderButton";
import i18next from "i18next";
import * as Util from "./Util";
import {authConfig} from "./Auth";
import * as ApplicationBackend from "../backend/ApplicationBackend";
import * as AgreementModal from "../common/modal/AgreementModal";
import {SendCodeInput} from "../common/SendCodeInput";
import RegionSelect from "../common/select/RegionSelect";
import CustomGithubCorner from "../common/CustomGithubCorner";
// import LanguageSelect from "../common/select/LanguageSelect";
import {withRouter} from "react-router-dom";
// import {CountryCodeSelect} from "../common/select/CountryCodeSelect";
import * as PasswordChecker from "../common/PasswordChecker";

class SignupPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      classes: props,
      applicationName: (props.applicationName ?? props.match?.params?.applicationName) ?? null,
      email: "",
      phone: "",
      countryCode: "",
      emailCode: "",
      phoneCode: "",
      validEmail: false,
      validPhone: false,
      region: "",
      isTermsOfUseVisible: false,
      termsOfUseContent: "",
      loginMethod: Setting.getLanguage() === "zh" ? "phone" : "email",
      // loginMethod: "phone",
      passwordAtLeast8: false,
      passwordLetterAndNumber: false,
    };
    this.form = React.createRef();
  }

  componentDidMount() {
    const oAuthParams = Util.getOAuthGetParameters();
    if (oAuthParams !== null) {
      const signinUrl = window.location.href.replace("/signup/oauth/authorize", "/login/oauth/authorize");
      sessionStorage.setItem("signinUrl", signinUrl);
    }
    if (this.getApplicationObj() === undefined) {
      if (this.state.applicationName !== null) {
        this.getApplication(this.state.applicationName);
      } else if (oAuthParams !== null) {
        this.getApplicationLogin(oAuthParams);
      } else {
        Setting.showMessage("error", `Unknown application name: ${this.state.applicationName}`);
        this.onUpdateApplication(null);
      }
    }
  }

  getApplication(applicationName) {
    if (applicationName === undefined) {
      return;
    }
    ApplicationBackend.getApplication("admin", applicationName)
      .then((application) => {
        this.onUpdateApplication(application);
      });
  }

  getApplicationLogin(oAuthParams) {
    AuthBackend.getApplicationLogin(oAuthParams)
      .then((res) => {
        if (res.status === "ok") {
          const application = res.data;
          this.onUpdateApplication(application);
        } else {
          this.onUpdateApplication(null);
          this.setState({
            msg: res.msg,
          });
        }
      });
  }

  getResultPath(application) {
    if (authConfig.appName === application.name) {
      return "/result";
    } else {
      if (Setting.hasPromptPage(application)) {
        return `/prompt/${application.name}`;
      } else {
        return `/result/${application.name}`;
      }
    }
  }

  getApplicationObj() {
    return this.props.application;
  }

  onUpdateAccount(account) {
    this.props.onUpdateAccount(account);
  }

  onUpdateApplication(application) {
    this.props.onUpdateApplication(application);
  }

  parseOffset(offset) {
    if (offset === 2 || offset === 4 || Setting.inIframe() || Setting.isMobile()) {
      return "0 auto";
    }
    if (offset === 1) {
      return "0 10%";
    }
    if (offset === 3) {
      return "0 60%";
    }
  }

  renderMethodChoiceBox() {
    const isZh = Setting.getLanguage() === "zh";
    const items = [];
    if (isZh) {
      items.push({label: i18next.t("signup:Phone registration"), key: "phone", children: this.renderPhoneForm()});
    }
    // items.push({label: i18next.t("signup:Phone registration"), key: "phone"});
    items.push({label: i18next.t("signup:Email registration"), key: "email", children: this.renderEmailOrUsernameForm()});
    // const defaultActiveKey = isZh ? "phone" : "email";
    return (
      <div>
        <Tabs
          items={items}
          // defaultActiveKey={defaultActiveKey}
          destroyInactiveTabPane
          onChange={(key) => {
            this.form.current.setFieldValue("username", "");
            this.setState({
              loginMethod: key,
              username: "",
            });
          }}
          centered
          tabBarGutter={90}
        >
        </Tabs>
      </div>
    );
  }

  onFinish(values, method) {
    const application = this.getApplicationObj();

    const params = new URLSearchParams(window.location.search);
    values["plan"] = params.get("plan");
    values["pricing"] = params.get("pricing");
    values["application"] = application.name;
    values["organization"] = application.organization;
    if (method === "phone") {
      values["username"] = `@JK_${values["phone"]}`;
      values["name"] = `@JK_${values["phone"]}`;
      values["password"] = `@JK_${values["phone"]}`;
    } else {
      const name = `@JK_${values["email"]}`;
      // const name = `jinko_${values["email"]}`.replace(/\.|-|@/gi, "_");
      values["username"] = name;
      values["name"] = name;
    }
    AuthBackend.signup(values)
      .then((res) => {
        if (res.status === "ok") {
          if (Setting.hasPromptPage(application)) {
            AuthBackend.getAccount("")
              .then((res) => {
                let account = null;
                if (res.status === "ok") {
                  account = res.data;
                  account.organization = res.data2;

                  this.onUpdateAccount(account);
                  Setting.goToLinkSoft(this, this.getResultPath(application));
                } else {
                  Setting.showMessage("error", `${i18next.t("application:Failed to sign in")}: ${res.msg}`);
                }
              });
          } else {
            Setting.goToLinkSoft(this, this.getResultPath(application));
          }
        } else {
          Setting.showMessage("error", i18next.t(`signup:${res.msg}`));
        }
      });
  }

  onFinishFailed(values, errorFields, outOfDate) {
    this.form.current.scrollToField(errorFields[0].name);
  }

  isProviderVisible(providerItem) {
    return Setting.isProviderVisibleForSignUp(providerItem);
  }

  // renderFormItem(application, signupItem) {
  //   if (!signupItem.visible) {
  //     return null;
  //   }

  //   const required = signupItem.required;

  //   if (signupItem.name === "Username") {
  //     return (
  //       <Form.Item
  //         name="username"
  //         rules={[
  //           {
  //             required: required,
  //             message: i18next.t("forget:Please input your username!"),
  //             whitespace: true,
  //           },
  //         ]}
  //       >
  //         <Input placeholder={i18next.t("signup:Username")} />
  //       </Form.Item>
  //     );
  //   } else if (signupItem.name === "Display name") {
  //     if (signupItem.rule === "First, last" && Setting.getLanguage() !== "zh") {
  //       return (
  //         <React.Fragment>
  //           <Form.Item
  //             name="firstName"
  //             rules={[
  //               {
  //                 required: required,
  //                 message: i18next.t("signup:Please input your first name!"),
  //                 whitespace: true,
  //               },
  //             ]}
  //           >
  //             <Input placeholder={i18next.t("general:First name")} />
  //           </Form.Item>
  //           <Form.Item
  //             name="lastName"
  //             rules={[
  //               {
  //                 required: required,
  //                 message: i18next.t("signup:Please input your last name!"),
  //                 whitespace: true,
  //               },
  //             ]}
  //           >
  //             <Input placeholder={i18next.t("general:Last name")} />
  //           </Form.Item>
  //         </React.Fragment>
  //       );
  //     }

  //     return (
  //       <Form.Item
  //         name="name"
  //         rules={[
  //           {
  //             required: required,
  //             message: (signupItem.rule === "Real name" || signupItem.rule === "First, last") ? i18next.t("signup:Please input your real name!") : i18next.t("signup:Please input your display name!"),
  //             whitespace: true,
  //           },
  //         ]}
  //       >
  //         <Input placeholder={(signupItem.rule === "Real name" || signupItem.rule === "First, last") ? i18next.t("general:Real name") : i18next.t("general:Display name")} />
  //       </Form.Item>
  //     );
  //   } else if (signupItem.name === "Affiliation") {
  //     return (
  //       <Form.Item
  //         name="affiliation"
  //         rules={[
  //           {
  //             required: required,
  //             message: i18next.t("signup:Please input your affiliation!"),
  //             whitespace: true,
  //           },
  //         ]}
  //       >
  //         <Input placeholder={i18next.t("user:Affiliation")} />
  //       </Form.Item>
  //     );
  //   } else if (signupItem.name === "ID card") {
  //     return (
  //       <Form.Item
  //         name="idCard"
  //         rules={[
  //           {
  //             required: required,
  //             message: i18next.t("signup:Please input your ID card number!"),
  //             whitespace: true,
  //           },
  //           {
  //             required: required,
  //             pattern: new RegExp(/^[1-9]\d{5}(18|19|20)\d{2}((0[1-9])|(10|11|12))(([0-2][1-9])|10|20|30|31)\d{3}[0-9X]$/, "g"),
  //             message: i18next.t("signup:Please input the correct ID card number!"),
  //           },
  //         ]}
  //       >
  //         <Input placeholder={i18next.t("user:ID card")} />
  //       </Form.Item>
  //     );
  //   } else if (signupItem.name === "Country/Region") {
  //     return (
  //       <Form.Item
  //         name="country_region"
  //         rules={[
  //           {
  //             required: required,
  //             message: i18next.t("signup:Please select your country/region!"),
  //           },
  //         ]}
  //       >
  //         <RegionSelect placeholder={i18next.t("user:Country/Region")} onChange={(value) => {this.setState({region: value});}} />
  //       </Form.Item>
  //     );
  //   } else if (signupItem.name === "Email") {
  //     return (
  //       <React.Fragment>
  //         <Form.Item
  //           name="email"
  //           rules={[
  //             {
  //               required: required,
  //               message: i18next.t("signup:Please input your Email!"),
  //             },
  //             {
  //               validator: (_, value) => {
  //                 if (this.state.email !== "" && !Setting.isValidEmail(this.state.email)) {
  //                   this.setState({validEmail: false});
  //                   return Promise.reject(i18next.t("signup:The input is not valid Email!"));
  //                 }

  //                 this.setState({validEmail: true});
  //                 return Promise.resolve();
  //               },
  //             },
  //           ]}
  //         >
  //           <Input placeholder={i18next.t("general:Email")} onChange={e => this.setState({email: e.target.value})} />
  //         </Form.Item>
  //         {
  //           signupItem.rule !== "No verification" &&
  //           <Form.Item
  //             name="emailCode"
  //             rules={[{
  //               required: required,
  //               message: i18next.t("code:Please input your verification code!"),
  //             }]}
  //           >
  //             <SendCodeInput
  //               disabled={!this.state.validEmail}
  //               method={"signup"}
  //               placeholder={i18next.t("code:Email code")}
  //               onButtonClickArgs={[this.state.email, "email", Setting.getApplicationName(application)]}
  //               application={application}
  //             />
  //           </Form.Item>
  //         }
  //       </React.Fragment>
  //     );
  //   } else if (signupItem.name === "Phone") {
  //     return (
  //       <React.Fragment>
  //         <Form.Item label={i18next.t("general:Phone")} required={required}>
  //           <Input.Group compact>
  //             <Form.Item
  //               name="countryCode"
  //               noStyle
  //               rules={[
  //                 {
  //                   required: required,
  //                   message: i18next.t("signup:Please select your country code!"),
  //                 },
  //               ]}
  //             >
  //               <CountryCodeSelect
  //                 style={{width: "35%"}}
  //                 countryCodes={this.getApplicationObj().organizationObj.countryCodes}
  //               />
  //             </Form.Item>
  //             <Form.Item
  //               name="phone"
  //               dependencies={["countryCode"]}
  //               noStyle
  //               rules={[
  //                 {
  //                   required: required,
  //                   message: i18next.t("signup:Please input your phone number!"),
  //                 },
  //                 ({getFieldValue}) => ({
  //                   validator: (_, value) => {
  //                     if (!required && !value) {
  //                       return Promise.resolve();
  //                     }

  //                     if (value && !Setting.isValidPhone(value, getFieldValue("countryCode"))) {
  //                       this.setState({validPhone: false});
  //                       return Promise.reject(i18next.t("signup:The input is not valid Phone!"));
  //                     }
  //                     this.setState({validPhone: true});
  //                     return Promise.resolve();
  //                   },
  //                 }),
  //               ]}
  //             >
  //               <Input
  //                 style={{width: "65%"}}
  //                 onChange={e => this.setState({phone: e.target.value})}
  //               />
  //             </Form.Item>
  //           </Input.Group>
  //         </Form.Item>
  //         {
  //           signupItem.rule !== "No verification" &&
  //           <Form.Item
  //             name="phoneCode"
  //             label={i18next.t("code:Phone code")}
  //             rules={[
  //               {
  //                 required: required,
  //                 message: i18next.t("code:Please input your phone verification code!"),
  //               },
  //             ]}
  //           >
  //             <SendCodeInput
  //               disabled={!this.state.validPhone}
  //               method={"signup"}
  //               onButtonClickArgs={[this.state.phone, "phone", Setting.getApplicationName(application)]}
  //               application={application}
  //               countryCode={this.form.current?.getFieldValue("countryCode")}
  //             />
  //           </Form.Item>
  //         }
  //       </React.Fragment>
  //     );
  //   } else if (signupItem.name === "Password") {
  //     return (
  //       <Form.Item
  //         name="password"
  //         rules={[
  //           {
  //             required: required,
  //             validateTrigger: "onChange",
  //             validator: (rule, value) => {
  //               const errorMsg = PasswordChecker.checkPasswordComplexity(value, application.organizationObj.passwordOptions);
  //               if (errorMsg === "") {
  //                 return Promise.resolve();
  //               } else {
  //                 return Promise.reject(errorMsg);
  //               }
  //             },
  //           },
  //         ]}
  //         hasFeedback
  //       >
  //         <Input.Password bordered={false} placeholder={i18next.t("general:Password")} />
  //       </Form.Item>
  //     );
  //   } else if (signupItem.name === "Confirm password") {
  //     return (
  //       <Form.Item
  //         name="confirm"
  //         label={i18next.t("signup:Confirm")}
  //         dependencies={["password"]}
  //         hasFeedback
  //         rules={[
  //           {
  //             required: required,
  //             message: i18next.t("signup:Please confirm your password!"),
  //           },
  //           ({getFieldValue}) => ({
  //             validator(rule, value) {
  //               if (!value || getFieldValue("password") === value) {
  //                 return Promise.resolve();
  //               }
  //               return Promise.reject(i18next.t("signup:Your confirmed password is inconsistent with the password!"));
  //             },
  //           }),
  //         ]}
  //       >
  //         <Input.Password />
  //       </Form.Item>
  //     );
  //   } else if (signupItem.name === "Agreement") {
  //     return AgreementModal.renderAgreementFormItem(application, required, {}, this);
  //   }
  // }

  renderFormByMethod(application) {
    const {loginMethod, passwordAtLeast8, passwordLetterAndNumber} = this.state;
    if (loginMethod === "phone") {
      return (
        <>
          <Form.Item
            name="affiliation"
            rules={[
              {
                required: !Setting.isWechat(),
                message: i18next.t("signup:Please input your Company Name!"),
              },
            ]}
          >
            <Input
              placeholder={i18next.t("general:Company Name")}
              bordered={false}
            />
          </Form.Item>
          <Form.Item
            name="countryCode"
            className="signUpCountry"
            rules={[
              {
                required: true,
                message: i18next.t("signup:Please select your country/region!"),
              },
            ]}
          >
            <RegionSelect
              bordered={false}
              placeholder={i18next.t("user:Country/Region")}
              defaultValue="CN"
              // onChange={(value) => {this.setState({region: value});}}
            />
          </Form.Item>
          <Form.Item
            name="phone"
            dependencies={["countryCode"]}
            // noStyle
            rules={[
              {
                required: true,
                message: i18next.t("signup:Please input your phone number!"),
              },
              ({getFieldValue}) => ({
                validator: (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }

                  if (value && !Setting.isValidPhone(value, getFieldValue("country_region"))) {
                    this.setState({validPhone: false});
                    return Promise.reject(i18next.t("signup:The input is not valid Phone!"));
                  }

                  this.setState({validPhone: true});
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input
              placeholder={i18next.t("general:Phone")}
              bordered={false}
              onChange={e => this.setState({phone: e.target.value})}
            />
          </Form.Item>
          <Form.Item
            name="phoneCode"
            rules={[
              {
                required: true,
                message: i18next.t("code:Please input your phone verification code!"),
              },
            ]}
          >
            <SendCodeInput
              disabled={!this.state.validPhone}
              method={"signup"}
              onButtonClickArgs={[this.state.phone, "phone", Setting.getApplicationName(application)]}
              application={application}
              countryCode={this.form.current?.getFieldValue("countryCode")}
            />
          </Form.Item>
          {/* <div style={{textAlign: "left", fontSize: "16px", color: "#333"}}>{i18next.t("code:Please select your user type")}：</div>
          <Form.Item
            name="type"
            rules={[
              {
                required: true,
                message: `${i18next.t("code:Please select your user type")}！`,
              },
            ]}
          >
            <Radio.Group>
              <Radio value="companies">{i18next.t("code:Company Account")}</Radio>
              <Radio value="personal">{i18next.t("code:Personal Account")}</Radio>
            </Radio.Group>
          </Form.Item> */}
        </>
      );
    } else {
      return (
        <>
          <Form.Item
            name="affiliation"
            rules={[
              {
                required: !Setting.isWechat(),
                message: i18next.t("signup:Please input your Company Name!"),
              },
            ]}
          >
            <Input
              placeholder={i18next.t("general:Company Name")}
              bordered={false}
            />
          </Form.Item>
          <Form.Item
            name="email"
            rules={[
              {
                required: true,
                message: i18next.t("signup:Please input your Email!"),
              },
              {
                validator: (_, value) => {
                  if (this.state.email !== "" && !Setting.isValidEmail(this.state.email)) {
                    this.setState({validEmail: false});
                    return Promise.reject(i18next.t("signup:The input is not valid Email!"));
                  }

                  this.setState({validEmail: true});
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder={i18next.t("general:Email")}
              bordered={false}
              onChange={e => this.setState({email: e.target.value})}
            />
          </Form.Item>
          <Form.Item
            name="emailCode"
            rules={[{
              required: true,
              message: i18next.t("code:Please input your verification code!"),
            }]}
          >
            <SendCodeInput
              disabled={!this.state.validEmail}
              method={"signup"}
              placeholder={i18next.t("code:Email code")}
              onButtonClickArgs={[this.state.email, "email", Setting.getApplicationName(application)]}
              application={application}
            />
          </Form.Item>
          <Form.Item
            name="password"
            className="password firstPassword"
            rules={[
              // {
              //   required: true,
              //   message: i18next.t("signup:Please input your password!"),
              // },
              () => ({
                validator: (_, value) => {
                  if (value) {
                    const errorMsgAtLeast8 = PasswordChecker.checkPasswordComplexity(value, ["AtLeast8"]);
                    const errorMsgLetterAndNumber = PasswordChecker.checkPasswordComplexity(value, ["LetterAndNumber"]);
                    this.setState({
                      passwordAtLeast8: !errorMsgAtLeast8,
                      passwordLetterAndNumber: !errorMsgLetterAndNumber,
                    });
                    if (errorMsgAtLeast8 === "" && errorMsgLetterAndNumber === "") {
                      return Promise.resolve();
                    } else {
                      return Promise.reject("");
                    }
                  }
                  return Promise.reject(i18next.t("signup:Please input your password!"));
                },
              }),
            ]}
            hasFeedback
          >
            <Input.Password bordered={false} placeholder={i18next.t("general:Password")} />
          </Form.Item>
          <div className="passwordMsg-wrapper">
            <div className="passwordMsg">
              {passwordAtLeast8 ? <CheckOutlined style={{color: "#18933B"}} /> : <MinusOutlined />}
              {i18next.t("user:The password must have at least 8 characters")}
            </div>
            <div className="passwordMsg">
              {passwordLetterAndNumber ? <CheckOutlined style={{color: "#18933B"}} /> : <MinusOutlined />}
              {i18next.t("user:The password must contain a letter and a number")}
            </div>
          </div>
          <Form.Item
            name="confirm"
            dependencies={["password"]}
            hasFeedback
            className="password"
            rules={[
              {
                required: true,
                message: i18next.t("signup:Please confirm your password!"),
              },
              ({getFieldValue}) => ({
                validator(rule, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(i18next.t("signup:Your confirmed password is inconsistent with the password!"));
                },
              }),
            ]}
          >
            <Input.Password bordered={false} placeholder={i18next.t("signup:Confirm")} />
          </Form.Item>
          {/* <div style={{textAlign: "left", fontSize: "16px", color: "#333"}}>{i18next.t("code:Please select your user type")}：</div>
          <Form.Item
            name="type"
            rules={[
              {
                required: true,
                message: `${i18next.t("code:Please select your user type")}！`,
              },
            ]}
          >
            <Radio.Group>
              <Radio value="companies">{i18next.t("code:Company Account")}</Radio>
              <Radio value="personal">{i18next.t("code:Personal Account")}</Radio>
            </Radio.Group>
          </Form.Item> */}
        </>
      );
    }
  }

  renderPhoneForm() {
    const application = this.getApplicationObj();
    return (
      <Form
        ref={this.form}
        name="signup"
        onFinish={(values) => this.onFinish(values, "phone")}
        onFinishFailed={(errorInfo) => this.onFinishFailed(errorInfo.values, errorInfo.errorFields, errorInfo.outOfDate)}
        initialValues={{
          application: application.name,
          organization: application.organization,
          countryCode: "CN",
          country_region: "CN",
        }}
        size="large"
        layout={Setting.isMobile() ? "vertical" : "horizontal"}
        style={{width: Setting.isMobile() ? "300px" : "360px"}}
      >
        <Form.Item
          name="affiliation"
          rules={[
            {
              required: !Setting.isWechat(),
              message: i18next.t("signup:Please input your Company Name!"),
            },
          ]}
        >
          <Input
            placeholder={i18next.t("general:Company Name")}
            bordered={false}
          />
        </Form.Item>
        <Form.Item
          name="countryCode"
          className="signUpCountry"
          rules={[
            {
              required: true,
              message: i18next.t("signup:Please select your country/region!"),
            },
          ]}
        >
          <RegionSelect
            bordered={false}
            placeholder={i18next.t("user:Country/Region")}
            defaultValue="CN"
            // onChange={(value) => {this.setState({region: value});}}
          />
        </Form.Item>
        <Form.Item
          name="phone"
          dependencies={["countryCode"]}
          // noStyle
          rules={[
            {
              required: true,
              message: i18next.t("signup:Please input your phone number!"),
            },
            ({getFieldValue}) => ({
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }

                if (value && !Setting.isValidPhone(value, getFieldValue("country_region"))) {
                  this.setState({validPhone: false});
                  return Promise.reject(i18next.t("signup:The input is not valid Phone!"));
                }

                this.setState({validPhone: true});
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input
            placeholder={i18next.t("general:Phone")}
            bordered={false}
            onChange={e => this.setState({phone: e.target.value})}
          />
        </Form.Item>
        <Form.Item
          name="phoneCode"
          rules={[
            {
              required: true,
              message: i18next.t("code:Please input your phone verification code!"),
            },
          ]}
        >
          <SendCodeInput
            disabled={!this.state.validPhone}
            method={"signup"}
            onButtonClickArgs={[this.state.phone, "phone", Setting.getApplicationName(application)]}
            application={application}
            countryCode={this.form.current?.getFieldValue("countryCode")}
          />
        </Form.Item>
        {/* <div style={{textAlign: "left", fontSize: "16px", color: "#333"}}>{i18next.t("code:Please select your user type")}：</div>
        <Form.Item
          name="type"
          rules={[
            {
              required: true,
              message: `${i18next.t("code:Please select your user type")}！`,
            },
          ]}
        >
          <Radio.Group>
            <Radio value="companies">{i18next.t("code:Company Account")}</Radio>
            <Radio value="personal">{i18next.t("code:Personal Account")}</Radio>
          </Radio.Group>
        </Form.Item> */}
        {/* {
          application.signupItems?.map(signupItem => this.renderFormItem(application, signupItem))
        } */}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            style={{
              width: "100%",
              marginBottom: "5px",
              background: "linear-gradient(to right, #71B9A4, #318545)",
              color: "white",
              border: "none",
            }}
          >
            {i18next.t("button:Sign Up")}
          </Button>
        </Form.Item>
        {AgreementModal.renderAgreementFormItem(application, true, {}, this, this.state.loginMethod)}
        <Form.Item>
          {/* <div style={{textAlign: "left", marginBottom: "4px"}}>
            <Checkbox style={{float: "left"}} disabled={!application.enablePassword}>
              {i18next.t("signup:I have read and agree")}
              <a style={{color: "#3677FF"}}>{i18next.t("login:《Privacy Policy》")}</a>
            </Checkbox>
          </div> */}
          <div style={{textAlign: "left"}}>
            {i18next.t("signup:Have Jinko account?")}&nbsp;{i18next.t("login:Go")}&nbsp;
            <a onClick={() => {
              const linkInStorage = sessionStorage.getItem("signinUrl");
              if (linkInStorage !== null && linkInStorage !== "") {
                Setting.goToLink(linkInStorage);
              } else {
                Setting.redirectToLoginPage(application, this.props.history);
              }
            }}>
              {`${i18next.t("login:Sign In")}>`}
            </a>
          </div>
        </Form.Item>
        {/* {
          application.providers.filter(providerItem => this.isProviderVisible(providerItem)).map(providerItem => {
            return ProviderButton.renderProviderLogo(providerItem.provider, application, 30, 5, "small", this.props.location);
          })
        } */}
      </Form>
    );
  }

  renderEmailOrUsernameForm() {
    const application = this.getApplicationObj();
    const {passwordAtLeast8, passwordLetterAndNumber} = this.state;
    return (
      <Form
        ref={this.form}
        name="signup"
        onFinish={(values) => this.onFinish(values, "email")}
        onFinishFailed={(errorInfo) => this.onFinishFailed(errorInfo.values, errorInfo.errorFields, errorInfo.outOfDate)}
        initialValues={{
          application: application.name,
          organization: application.organization,
          countryCode: "CN",
          country_region: "CN",
        }}
        size="large"
        layout={Setting.isMobile() ? "vertical" : "horizontal"}
        style={{width: Setting.isMobile() ? "300px" : "360px"}}
      >
        <Form.Item
          name="affiliation"
          rules={[
            {
              required: !Setting.isWechat(),
              message: i18next.t("signup:Please input your Company Name!"),
            },
          ]}
        >
          <Input
            placeholder={i18next.t("general:Company Name")}
            bordered={false}
          />
        </Form.Item>
        <Form.Item
          name="email"
          rules={[
            {
              required: true,
              message: i18next.t("signup:Please input your Email!"),
            },
            {
              validator: (_, value) => {
                if (this.state.email !== "" && !Setting.isValidEmail(this.state.email)) {
                  this.setState({validEmail: false});
                  return Promise.reject(i18next.t("signup:The input is not valid Email!"));
                }

                this.setState({validEmail: true});
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder={i18next.t("general:Email")}
            bordered={false}
            onChange={e => this.setState({email: e.target.value})}
          />
        </Form.Item>
        <Form.Item
          name="emailCode"
          rules={[{
            required: true,
            message: i18next.t("code:Please input your verification code!"),
          }]}
        >
          <SendCodeInput
            disabled={!this.state.validEmail}
            method={"signup"}
            placeholder={i18next.t("code:Email code")}
            onButtonClickArgs={[this.state.email, "email", Setting.getApplicationName(application)]}
            application={application}
          />
        </Form.Item>
        <Form.Item
          name="password"
          className="password firstPassword"
          rules={[
            // {
            //   required: true,
            //   message: i18next.t("signup:Please input your password!"),
            // },
            () => ({
              validator: (_, value) => {
                if (value) {
                  const errorMsgAtLeast8 = PasswordChecker.checkPasswordComplexity(value, ["AtLeast8"]);
                  const errorMsgLetterAndNumber = PasswordChecker.checkPasswordComplexity(value, ["LetterAndNumber"]);
                  this.setState({
                    passwordAtLeast8: !errorMsgAtLeast8,
                    passwordLetterAndNumber: !errorMsgLetterAndNumber,
                  });
                  if (errorMsgAtLeast8 === "" && errorMsgLetterAndNumber === "") {
                    return Promise.resolve();
                  } else {
                    return Promise.reject("");
                  }
                }
                return Promise.reject(i18next.t("signup:Please input your password!"));
              },
            }),
          ]}
          hasFeedback
        >
          <Input.Password bordered={false} placeholder={i18next.t("general:Password")} />
        </Form.Item>
        <div className="passwordMsg-wrapper">
          <div className="passwordMsg">
            {passwordAtLeast8 ? <CheckOutlined style={{color: "#18933B"}} /> : <MinusOutlined />}
            {i18next.t("user:The password must have at least 8 characters")}
          </div>
          <div className="passwordMsg">
            {passwordLetterAndNumber ? <CheckOutlined style={{color: "#18933B"}} /> : <MinusOutlined />}
            {i18next.t("user:The password must contain a letter and a number")}
          </div>
        </div>
        <Form.Item
          name="confirm"
          dependencies={["password"]}
          hasFeedback
          className="password"
          rules={[
            {
              required: true,
              message: i18next.t("signup:Please confirm your password!"),
            },
            ({getFieldValue}) => ({
              validator(rule, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(i18next.t("signup:Your confirmed password is inconsistent with the password!"));
              },
            }),
          ]}
        >
          <Input.Password bordered={false} placeholder={i18next.t("signup:Confirm")} />
        </Form.Item>
        {/* <div style={{textAlign: "left", fontSize: "16px", color: "#333"}}>{i18next.t("code:Please select your user type")}：</div>
        <Form.Item
          name="type"
          rules={[
            {
              required: true,
              message: `${i18next.t("code:Please select your user type")}！`,
            },
          ]}
        >
          <Radio.Group>
            <Radio value="companies">{i18next.t("code:Company Account")}</Radio>
            <Radio value="personal">{i18next.t("code:Personal Account")}</Radio>
          </Radio.Group>
        </Form.Item> */}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            style={{
              width: "100%",
              marginBottom: "5px",
              background: "linear-gradient(to right, #71B9A4, #318545)",
              color: "white",
              border: "none",
            }}
          >
            {i18next.t("button:Sign Up")}
          </Button>
        </Form.Item>
        {AgreementModal.renderAgreementFormItem(application, true, {}, this, this.state.loginMethod)}
        <Form.Item>
          {/* <div style={{textAlign: "left", marginBottom: "4px"}}>
            <Checkbox style={{float: "left"}} disabled={!application.enablePassword}>
              {i18next.t("signup:I have read and agree")}
              <a style={{color: "#3677FF"}}>{i18next.t("login:《Privacy Policy》")}</a>
            </Checkbox>
          </div> */}
          <div style={{textAlign: "left"}}>
            {i18next.t("signup:Have Jinko account?")}&nbsp;{i18next.t("login:Go")}&nbsp;
            <a onClick={() => {
              const linkInStorage = sessionStorage.getItem("signinUrl");
              if (linkInStorage !== null && linkInStorage !== "") {
                Setting.goToLink(linkInStorage);
              } else {
                Setting.redirectToLoginPage(application, this.props.history);
              }
            }}>
              {`${i18next.t("login:Sign In")}>`}
            </a>
          </div>
        </Form.Item>
        {/* {
          application.providers.filter(providerItem => this.isProviderVisible(providerItem)).map(providerItem => {
            return ProviderButton.renderProviderLogo(providerItem.provider, application, 30, 5, "small", this.props.location);
          })
        } */}
      </Form>
    );
  }

  render() {
    const application = this.getApplicationObj();
    if (application === undefined || application === null) {
      return null;
    }

    if (application.signupHtml !== "") {
      return (
        <div dangerouslySetInnerHTML={{__html: application.signupHtml}} />
      );
    }

    return (
      <React.Fragment>
        <CustomGithubCorner />
        {/* <div className="login-content" style={{margin: this.props.preview ?? this.parseOffset(application.formOffset)}}> */}
        <div className="login-content" style={{margin: "0 auto"}}>
          {/* {Setting.inIframe() || Setting.isMobile() ? null : <div dangerouslySetInnerHTML={{__html: application.formCss}} />} */}
          {Setting.inIframe() || !Setting.isMobile() ? null : <div dangerouslySetInnerHTML={{__html: application.formCssMobile}} />}
          <div className="login-panel" >
            <div className="side-image" style={{display: application.formOffset !== 4 ? "none" : null}}>
              <div dangerouslySetInnerHTML={{__html: application.formSideHtml}} />
            </div>
            <div className="login-form">
              {/* {
                Setting.renderHelmet(application)
              } */}
              {/* {
                Setting.renderLogo(application)
              } */}
              {/* <LanguageSelect languages={application.organizationObj.languages} style={{top: "55px", right: "5px", position: "absolute"}} /> */}
              {this.renderMethodChoiceBox()}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(SignupPage);
